import React from "react";
import { Check, X } from "lucide-react";
import { Brand } from "../BookIt/StepShell";
import { linkButton, solidButton } from "./ui";

// Spacing uses explicit px values: Bootstrap is loaded globally and its
// !important utilities (gap-5, px-4, pt-3...) override Tailwind's of the same
// name. Headings set the body font because the global h1/h2 rules default to
// the condensed display face.
const EYEBROW =
  "m-0 font-[Roboto] text-[11px] font-semibold uppercase leading-normal tracking-[0.12em]";
const HEADING =
  "m-0 font-[Roboto] text-[24px] font-bold leading-[1.2] short:text-[22px] text-slate-900";
const BODY = "m-0 text-[15px] leading-normal text-slate-600";
const LABEL = `${EYEBROW} text-slate-500`;

// The same card for every state: brand, then the title block and content.
// Blocks are 20px apart; things that belong together sit closer.
function Screen({ children }) {
  return (
    <div className="flex min-h-[100dvh] flex-col items-center justify-center bg-slate-100 px-[16px] py-[12px] short:justify-start short:py-[8px] font-[Roboto] text-slate-900 antialiased">
      <div className="flex w-full max-w-[480px] flex-col gap-[20px] rounded-[20px] border border-slate-200 bg-white p-[24px] shadow-sm sm:p-[32px] short:gap-[14px] short:p-[16px]">
        <Brand />
        <div className="flex flex-col gap-[20px] short:gap-[12px]">
          {children}
        </div>
      </div>
      <footer className="flex flex-col items-center gap-0 px-[16px] pb-[4px] pt-[8px] text-center text-[12px] short:pt-[6px] short:text-[11px] leading-normal text-slate-500">
        <span>Skywaze Inc. dba Cinesis</span>
        <span>2021 Fillmore St PMB 2294, San Francisco, CA 94115</span>
      </footer>
    </div>
  );
}

function Title({ children, subtitle, eyebrow = "Rate confirmations" }) {
  return (
    <div className="flex flex-col gap-[8px]">
      <div className="flex flex-col gap-[6px]">
        <p className={`${EYEBROW} text-indigo-600`}>{eyebrow}</p>
        <h1 className={HEADING}>{children}</h1>
      </div>
      {subtitle && <p className={BODY}>{subtitle}</p>}
    </div>
  );
}

export function Message({ title, body }) {
  return (
    <Screen>
      <Title eyebrow="Cinesis" subtitle={body}>
        {title}
      </Title>
    </Screen>
  );
}

function Point({ ok, children }) {
  const Icon = ok ? Check : X;
  return (
    <li className="flex items-start gap-[8px] text-[15px] leading-snug text-slate-900">
      <Icon
        size={16}
        aria-hidden="true"
        className={`mt-px shrink-0 ${ok ? "text-emerald-700" : "text-rose-600"}`}
      />
      <span>{children}</span>
    </li>
  );
}

function Group({ label, children }) {
  return (
    <section className="flex flex-col gap-[8px] short:gap-[6px]">
      <h2 className={LABEL}>{label}</h2>
      <ul className="m-0 flex list-none flex-col gap-[8px] p-0 short:gap-[4px]">
        {children}
      </ul>
    </section>
  );
}

// What we read and what we never do, before Google's own screen.
// Before Google is switched on, the button says what it really does: no
// Google screen follows.
export function ConnectExplain({
  companyName,
  notice,
  onConnect,
  busy,
  waitlist,
}) {
  const label = waitlist ? "Join the waitlist" : "Connect Gmail";
  return (
    <Screen>
      <Title
        subtitle={
          <>
            {companyName && (
              <>
                Hi {companyName},
                <br />
              </>
            )}
            Get your next offer faster. We pull rate cons from your inbox, so
            you can book again right away.
          </>
        }
      >
        Connect your Gmail
      </Title>
      {notice && (
        <p
          role="alert"
          className="m-0 rounded-[14px] border border-amber-200 bg-amber-50 px-[16px] py-[12px] text-[15px] leading-snug text-amber-950"
        >
          {notice}
        </p>
      )}
      <div className="flex flex-col gap-[14px] rounded-[14px] bg-slate-50 p-[16px] short:gap-[8px] short:px-[14px] short:py-[10px]">
        <Group label="What we read">
          <Point ok>
            Only broker emails with a rate confirmation attached.
          </Point>
        </Group>
        <div aria-hidden="true" className="h-px bg-slate-200" />
        <Group label="What we never do">
          <Point>Read your other email.</Point>
          <Point>Send email from your account.</Point>
          <Point>Delete or change anything.</Point>
        </Group>
      </div>
      <section className="flex flex-col gap-[6px]">
        <h2 className={LABEL}>Before you connect</h2>
        <p className="m-0 text-[14px] leading-normal text-slate-600 short:text-[13px] short:leading-snug">
          Google will ask you to allow “Read your email messages and settings.”
          That’s Google’s standard wording. We only use it to find rate
          confirmations for your loads, so you never have to forward them.
        </p>
      </section>
      <div className="flex flex-col gap-[10px] short:gap-[8px]">
        <button
          type="button"
          onClick={onConnect}
          disabled={busy}
          className={solidButton}
        >
          {busy ? "One moment…" : label}
        </button>
        <p className="m-0 text-center text-[12px] leading-normal text-slate-500">
          Free. Disconnect anytime.
        </p>
      </div>
    </Screen>
  );
}

export function Connected({ gmailAddress, onDisconnect, busy }) {
  return (
    <Screen>
      <Title
        subtitle={`We'll pick up the rate confirmations brokers send to ${
          gmailAddress || "your Gmail"
        }.`}
      >
        Gmail connected
      </Title>
      <div className="rounded-[14px] bg-slate-50 px-[14px] py-[12px]">
        <Group label="What happens now">
          <Point ok>
            We only read broker emails with a rate confirmation attached.
          </Point>
          <Point ok>We never send, delete or change anything.</Point>
          <Point ok>Disconnect anytime in your Google account.</Point>
        </Group>
      </div>
      {/* A tab opened from a text can't be closed by the page, so there's no
          "Done" button; disconnecting stays available as a quiet link. */}
      <div className="flex flex-col items-center gap-[6px] text-center">
        <p className="m-0 text-[15px] font-semibold text-slate-900">
          You're all set. You can close this page.
        </p>
        <button
          type="button"
          onClick={onDisconnect}
          disabled={busy}
          className={linkButton}
        >
          Disconnect Gmail
        </button>
      </div>
    </Screen>
  );
}

// "Join the waitlist" before the Google connection is switched on.
export function Waitlisted({ companyName }) {
  return (
    <Screen>
      <Title
        subtitle={`Thanks${
          companyName ? `, ${companyName}` : ""
        }. We're turning Gmail on for carriers in the order they sign up. We'll text you when yours is ready.`}
      >
        You're on the list
      </Title>
    </Screen>
  );
}
